import { TEST_ID_WATCHLIST } from "e2e/testId";
import { CheckIcon, LucideProps, PlusIcon } from "lucide-react-native";
import React, { memo } from "react";
import { TouchableWithoutFeedback } from "react-native-gesture-handler";

import BadgeComponent from "@/components/molecules/Badge";
import ManualPlayerBadge from "@/components/molecules/ManualPlayerBadge";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { CurrencyCode, Position, SortField } from "@/generated/graphql";
import { PlayerCardData } from "@/types/playerCardTypes";
import { getPositionAndTeamName } from "@/utils/getPlayerInformations";
import { RADIUS } from "@/config/radius";

/**
 * Info badge configuration
 */
export interface IInfoBadge {
  value?: number | null;
  min: number;
  max: number;
  type: SortField;
  isDefaultStyle?: boolean;
}
/**
 * Props for InfoPlayerView component
 */
interface InfoPlayerProps {
  item: PlayerCardData;
  loading: boolean;
  isActive: boolean;
  isPlayerInAnyWatchList?: boolean;
  drag?: () => void;
  infoBadge?: IInfoBadge;
  onPress?: () => void;
  onAddPress?: () => void;
  playerName: string;
  currencyCode: CurrencyCode;
  isSmallScreen: boolean;
  isDark: boolean;
}

/**
 * Add-to-watchlist button style.
 * design-system: dynamic-style waiver — data-driven value (watchlist/theme-driven background)
 * @param isPlayerInAnyWatchList - Whether the player is on a watchlist
 * @param isDark - Whether dark theme is active
 */
const addButtonStyle = (
  isPlayerInAnyWatchList: boolean | undefined,
  isDark: boolean
) => ({
  padding: 8,
  marginRight: 6,
  backgroundColor: isPlayerInAnyWatchList
    ? "#0b8dcd"
    : isDark
      ? "#26252A"
      : "#e5e5e5",
  borderRadius: RADIUS.full,
  justifyContent: "center" as const,
  alignItems: "center" as const,
});

/**
 * Add-button icon color.
 * design-system: runtime-value waiver — data-driven color (watchlist-driven)
 * @param isPlayerInAnyWatchList - Whether the player is on a watchlist
 */
const addIconColor = (isPlayerInAnyWatchList: boolean | undefined) =>
  isPlayerInAnyWatchList ? "#ffffff" : "#0b8dcd";

/** Add-button icon size in px (matches the previous Icon atom `size="md"` → h-4 w-4). */
const ADD_ICON_SIZE = 16;

/**
 * Pure view component for player info
 * @param root0 - Component props
 * @param root0.onPress - Handler for item press
 * @param root0.onAddPress - Handler for add button press
 * @param root0.drag - Handler for drag gesture
 * @param root0.loading - Whether data is loading
 * @param root0.isActive - Whether item is active
 * @param root0.playerName - Player display name
 * @param root0.item - Player data fragment
 * @param root0.infoBadge - Badge configuration
 * @param root0.isPlayerInAnyWatchList - Whether player is in any watchlist
 * @param root0.currencyCode - Currency code for formatting
 * @param root0.isSmallScreen - Whether device has small screen
 * @param root0.isDark - Whether dark theme is active
 */
const InfoPlayerView = ({
  onPress,
  onAddPress,
  drag,
  loading,
  isActive,
  playerName,
  item,
  infoBadge,
  isPlayerInAnyWatchList,
  currencyCode,
  isSmallScreen,
  isDark,
}: InfoPlayerProps) => (
  <TouchableWithoutFeedback
    testID={TEST_ID_WATCHLIST.MY_ROSTER_PLAYER_ITEM}
    onPress={onPress}
    onLongPress={drag}
    disabled={loading || isActive}
  >
    <Stack
      direction="horizontal"
      className={`items-center justify-between ${!isSmallScreen ? "gap-3" : "gap-4"}`}
    >
      {!isSmallScreen && (
        <TouchableWithoutFeedback
          // eslint-disable-next-line no-restricted-syntax -- gesture-handler TouchableWithoutFeedback nested in drag context; data-driven backgroundColor (watchlist membership + theme), non-atomizable
          style={addButtonStyle(isPlayerInAnyWatchList, isDark)}
          onPress={loading ? undefined : onAddPress}
        >
          {isPlayerInAnyWatchList ? (
            <CheckIcon
              size={ADD_ICON_SIZE}
              {...({
                color: addIconColor(isPlayerInAnyWatchList),
                strokeWidth: 3,
              } as Partial<LucideProps>)}
            />
          ) : (
            <PlusIcon
              size={ADD_ICON_SIZE}
              {...({
                color: addIconColor(isPlayerInAnyWatchList),
                strokeWidth: 3,
              } as Partial<LucideProps>)}
            />
          )}
        </TouchableWithoutFeedback>
      )}
      <Stack className="flex-1">
        <Text
          variant={!isSmallScreen ? "title-lg" : "display-sm"}
          className="shrink text-content-primary"
        >
          {loading ? "Please wait..." : playerName}
        </Text>
        <Stack direction="horizontal" className="flex-wrap items-center gap-1">
          <Text
            variant="unstyled"
            className={`font-medium ${!isSmallScreen ? "text-title-sm" : "text-title"} text-content-secondary`}
          >
            {getPositionAndTeamName(
              item?.positions as Position[],
              item.team?.name ?? item.teamName ?? undefined,
              loading
            )}
          </Text>
          <ManualPlayerBadge show={item?.isManual ?? false} compact />
        </Stack>
      </Stack>
      <Box className="items-end gap-2">
        {infoBadge && (
          <BadgeComponent
            type={infoBadge?.type}
            value={infoBadge.value === -1 ? null : infoBadge.value}
            min={infoBadge.min}
            max={infoBadge.max}
            currencyCode={currencyCode}
            isHigherBetter
            isDefaultStyle={infoBadge.isDefaultStyle}
          />
        )}
      </Box>
    </Stack>
  </TouchableWithoutFeedback>
);

InfoPlayerView.displayName = "InfoPlayerView";

export default memo(InfoPlayerView);
